import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicShell from "../components/public/PublicShell";
import { useSeo } from "../lib/useSeo";

const CSS = `
.agz-landing .public-page{padding:72px 0 48px}
.agz-landing .public-page h1{font-size:clamp(38px,5.5vw,60px);letter-spacing:-.035em;margin-bottom:22px;max-width:820px}
.agz-landing .public-page .eyebrow{display:inline-block;font-size:12px;font-weight:600;letter-spacing:.14em;color:var(--muted);text-transform:uppercase;margin-bottom:22px}
.agz-landing .public-page .intro{font-size:clamp(17px,1.6vw,20px);color:var(--ink-soft);line-height:1.55;max-width:720px;margin-bottom:56px}
.agz-landing .contact-grid{display:grid;grid-template-columns:1fr 1.15fr;gap:48px;align-items:start}
.agz-landing .contact-details{display:grid;gap:22px}
.agz-landing .cdi h4{font-size:12px;font-weight:600;letter-spacing:.14em;color:var(--muted);text-transform:uppercase;margin-bottom:8px}
.agz-landing .cdi a,.agz-landing .cdi p{color:var(--ink);font-size:17px;font-weight:500;text-decoration:none;line-height:1.4}
.agz-landing .cdi a:hover{color:var(--blue-d)}
.agz-landing .contact-form{background:var(--card);border:1px solid var(--line);border-radius:var(--r-card);padding:clamp(24px,3.5vw,36px);box-shadow:var(--sh-sm)}
.agz-landing .contact-form .row{display:grid;gap:14px}
.agz-landing .contact-form .row-2{grid-template-columns:1fr 1fr;gap:14px}
.agz-landing .field label{display:block;font-size:12.5px;font-weight:600;color:var(--ink-soft);margin-bottom:6px;letter-spacing:.02em}
.agz-landing .field input,.agz-landing .field textarea{width:100%;font-family:var(--sans);font-size:15px;color:var(--ink);background:var(--paper);border:1px solid var(--line);border-radius:var(--r-md);padding:12px 14px;transition:border-color .18s,box-shadow .2s;outline:none}
.agz-landing .field input:focus,.agz-landing .field textarea:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(11,103,255,.14)}
.agz-landing .field textarea{resize:vertical;min-height:132px;line-height:1.5}
.agz-landing .field .err{color:var(--coral-d);font-size:12.5px;margin-top:5px}
.agz-landing .consent{display:flex;align-items:flex-start;gap:10px;font-size:13px;color:var(--ink-soft);margin-top:6px;line-height:1.4}
.agz-landing .consent input{margin-top:3px;accent-color:var(--ink)}
.agz-landing .consent a{color:var(--ink);text-decoration:underline;text-underline-offset:2px}
.agz-landing .form-status{margin-top:14px;font-size:13.5px;padding:10px 14px;border-radius:var(--r-md);line-height:1.45}
.agz-landing .form-status.ok{background:#EAF6EE;color:#1B6B3F;border:1px solid #C4E2D0}
.agz-landing .form-status.err{background:#FBE9E7;color:#8F2E22;border:1px solid #F4C3B9}
.agz-landing .honeypot{position:absolute;left:-9999px;top:auto;width:1px;height:1px;opacity:0;pointer-events:none}
.agz-landing .back-home{display:inline-flex;align-items:center;gap:6px;font-size:13px;color:var(--ink-soft);margin-top:36px;text-decoration:none}
.agz-landing .back-home:hover{color:var(--ink)}
@media(max-width:820px){.agz-landing .contact-grid{grid-template-columns:1fr;gap:32px}.agz-landing .contact-form .row-2{grid-template-columns:1fr}}
@media(max-width:520px){.agz-landing .public-page{padding:48px 0 32px}}
`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Allow-list of query subjects that may prefill the Subject field.
// The value from the URL is never trusted directly.
const ALLOWED_QUERY_SUBJECTS = new Set(["Sales inquiry"]);

const sanitizeSubjectFromQuery = (raw) => {
  if (!raw) return "";
  // Strip control chars, collapse whitespace, cap at 160 (mirrors backend validator).
  const cleaned = String(raw)
    .split("")
    .filter((c) => c === "\t" || c === " " || (c.charCodeAt(0) >= 0x20 && c.charCodeAt(0) !== 0x7f))
    .join("")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);
  return ALLOWED_QUERY_SUBJECTS.has(cleaned) ? cleaned : "";
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const initialSubject = sanitizeSubjectFromQuery(searchParams.get("subject"));

  useSeo({
    title: "Contact Agenzee | Product, Support and Partnerships",
    description: "Contact Agenzee for product information, assistance, partnerships or general enquiries.",
    path: "/contact",
  });

  const [form, setForm] = useState({
    name: "", email: "", company: "", subject: initialSubject, message: "", consent: false,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ kind: "idle", msg: "" }); // idle | loading | ok | err
  const honeypotRef = useRef(null);

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    else if (form.name.length > 120) e.name = "Name is too long.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email.";
    if (form.company && form.company.length > 160) e.company = "Company name is too long.";
    if (!form.subject.trim()) e.subject = "Subject is required.";
    else if (form.subject.length > 160) e.subject = "Subject is too long.";
    if (!form.message.trim()) e.message = "Message is required.";
    else if (form.message.length > 5000) e.message = "Message is too long (5000 max).";
    if (!form.consent) e.consent = "Please accept the Privacy Policy to continue.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    // Honeypot: if a bot filled the hidden field, silently succeed (do NOT send).
    if (honeypotRef.current && honeypotRef.current.value) {
      setStatus({ kind: "ok", msg: "Thank you. Your message has been sent." });
      return;
    }
    if (!validate()) return;
    setStatus({ kind: "loading", msg: "Sending…" });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
          consent: form.consent,
        }),
      });
      if (!res.ok) {
        let msg = "We couldn't send your message. Please try again in a minute.";
        try { const j = await res.json(); if (j && j.detail) msg = String(j.detail); } catch (_) { /* keep default */ }
        setStatus({ kind: "err", msg });
        return;
      }
      setStatus({ kind: "ok", msg: "Thank you. Your message has been sent." });
      setForm({ name: "", email: "", company: "", subject: "", message: "", consent: false });
    } catch (_) {
      setStatus({ kind: "err", msg: "We couldn't reach the server. Please try again shortly." });
    }
  };

  return (
    <PublicShell>
      <style>{CSS}</style>
      <div className="public-page" data-testid="contact-page">
        <div className="wrap">
          <span className="eyebrow">Contact</span>
          <h1>Let&apos;s talk about what you are building.</h1>
          <p className="intro">
            Have a question about Agenzee, need assistance or want to explore a partnership? Send us a message and our team will get back to you.
          </p>

          <div className="contact-grid">
            <aside className="contact-details" aria-label="Contact details">
              <div className="cdi">
                <h4>Email</h4>
                <a href="mailto:info@agenzee.it" data-testid="contact-email-link">info@agenzee.it</a>
              </div>
              <div className="cdi">
                <h4>Location</h4>
                <p>Italy</p>
              </div>
              <div className="cdi">
                <h4>Instagram</h4>
                <a
                  href="https://www.instagram.com/_agenzee/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link">
                  @_agenzee
                </a>
              </div>
              <Link to="/" className="back-home" data-testid="contact-back-home">← Back to homepage</Link>
            </aside>

            <form className="contact-form" onSubmit={submit} noValidate data-testid="contact-form">
              {/* Honeypot — hidden from users, visible to naive bots */}
              <input
                ref={honeypotRef}
                className="honeypot"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <div className="row row-2">
                <div className="field">
                  <label htmlFor="ct-name">Name<span aria-hidden> *</span></label>
                  <input id="ct-name" type="text" value={form.name} onChange={upd("name")} maxLength={120} required aria-invalid={!!errors.name} data-testid="contact-name-input" />
                  {errors.name && <div className="err" role="alert">{errors.name}</div>}
                </div>
                <div className="field">
                  <label htmlFor="ct-email">Email<span aria-hidden> *</span></label>
                  <input id="ct-email" type="email" value={form.email} onChange={upd("email")} maxLength={200} required aria-invalid={!!errors.email} data-testid="contact-email-input" />
                  {errors.email && <div className="err" role="alert">{errors.email}</div>}
                </div>
              </div>
              <div className="row row-2" style={{ marginTop: 14 }}>
                <div className="field">
                  <label htmlFor="ct-company">Company</label>
                  <input id="ct-company" type="text" value={form.company} onChange={upd("company")} maxLength={160} aria-invalid={!!errors.company} data-testid="contact-company-input" />
                  {errors.company && <div className="err" role="alert">{errors.company}</div>}
                </div>
                <div className="field">
                  <label htmlFor="ct-subject">Subject<span aria-hidden> *</span></label>
                  <input id="ct-subject" type="text" value={form.subject} onChange={upd("subject")} maxLength={160} required aria-invalid={!!errors.subject} data-testid="contact-subject-input" />
                  {errors.subject && <div className="err" role="alert">{errors.subject}</div>}
                </div>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label htmlFor="ct-message">Message<span aria-hidden> *</span></label>
                <textarea id="ct-message" value={form.message} onChange={upd("message")} maxLength={5000} required aria-invalid={!!errors.message} data-testid="contact-message-input" />
                {errors.message && <div className="err" role="alert">{errors.message}</div>}
              </div>
              <label className="consent">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={upd("consent")}
                  aria-invalid={!!errors.consent}
                  data-testid="contact-consent-checkbox"
                />
                <span>
                  I agree to the processing of my data in accordance with the {" "}
                  <Link to="/privacy">Privacy Policy</Link>.
                </span>
              </label>
              {errors.consent && <div className="err" role="alert" style={{ marginTop: 4 }}>{errors.consent}</div>}

              <button
                type="submit"
                className="btn btn--primary btn--lg"
                style={{ marginTop: 20, width: "100%" }}
                disabled={status.kind === "loading"}
                data-testid="contact-submit-btn">
                {status.kind === "loading" ? "Sending…" : "Send message"}
              </button>

              <div className="form-status" aria-live="polite" role="status" style={{ display: status.kind === "ok" || status.kind === "err" ? "block" : "none" }}>
                {status.kind === "ok" && <span className="ok" data-testid="contact-success-msg">{status.msg}</span>}
                {status.kind === "err" && <span className="err" data-testid="contact-error-msg">{status.msg}</span>}
              </div>
              {/* Give the aria-live region correct visual style based on state */}
              <style>{`.agz-landing .form-status .ok{color:#1B6B3F}.agz-landing .form-status .err{color:#8F2E22}`}</style>
            </form>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
